import { Fragment, type RefObject, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Ban, Check, CircleDashed, CircleHelp, FileX2, Flame, History, Info, SquareFunction, TrendingUp, TriangleAlert, X, Zap } from "lucide-react";
import type { Dettaglio, Famiglia, Giudizio, NotaAnalisi, Segnalazione, VoceFamiglia } from "@/api/analisiBolletta";
import {
  avvisoMancano, circa, cifraVoce, cifreVoce, nomeVoce, ESTESA_CHIUDI, ESTESA_COLONNE, ESTESA_COLONNE_SENZA_GIUDIZIO, ESTESA_EURO_IN_PIU, ESTESA_IN_REGOLA, ESTESA_NESSUNA,
  ESTESA_NON_CONTROLLATA, ESTESA_NON_REGOLATA, ESTESA_NON_REGOLATA_TELEFONO, ESTESA_NON_VERIFICABILE, ESTESA_NOTA, ESTESA_PREZZO_LIBERO, ESTESA_TITOLO,
  dallaFormula, euro, GIUDIZIO, inBreve, NOTE_TITOLO, RIGHE_TITOLO, VOCI_DA_SEGNALARE, testoPerche, VOCI_EURO_IN_BOLLETTA, VOCI_NON_SPIEGATI, mesiBolletta, motivoFuoriPosto, motivoRiga, prezzoVoce,
  AI_ERRORE_GAS, avvisoLuce, avvisoPeriodo, IVA_ESCLUSA, motiviGas,
} from "@/lib/analisiScritte";
import { Perche, usePercheRiga } from "./Perche";
import { useVistaCliente } from "./vistaCliente";

// colori come nel report degli studi (12/10): gonfiata rossa, potrebbe non esserci ambra, non dovrebbe esserci azzurra
const PILLOLA: Record<Giudizio, { testo: string; icona: typeof Check; classi: string }> = {
  G: { testo: GIUDIZIO.G, icona: TrendingUp, classi: "text-red-800 bg-red-50" },
  N: { testo: GIUDIZIO.N, icona: Ban, classi: "text-blue-900 bg-blue-50" },
  // colore di «N» (stessa gravità), icona sua: la formula (21/10)
  V: { testo: GIUDIZIO.V, icona: SquareFunction, classi: "text-blue-900 bg-blue-50" },
  P: { testo: GIUDIZIO.P, icona: CircleHelp, classi: "text-amber-800 bg-amber-50" },
  da_verificare: { testo: GIUDIZIO.da_verificare, icona: CircleDashed, classi: "text-slate-700 bg-slate-100" },
  in_regola: { testo: ESTESA_IN_REGOLA, icona: Check, classi: "text-brand-dark bg-brand/10" },
};
const ORDINE: Giudizio[] = ["G", "N", "V", "P", "da_verificare", "in_regola"];
// l'ultimo riquadro, se sono dispari, prende sul telefono la riga intera
const A_TUTTA_RIGA = "col-span-2 sm:col-span-1";
// colonne dei riquadri sul computer (classi scritte per intero: Tailwind le cerca nel sorgente). Una pillola lunga
// («Non dovrebbe esserci») chiede circa 170 px: quattro in riga solo da lg, dove la finestra è larga; cinque mai,
// vanno 3 + 2 (prova in browser del 21/10: a 640-1280 px cinque riquadri da 97-143 px facevano uscire le pillole)
const COLONNE: Record<number, string> = { 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-3" };

/** `aCapo`: nella colonna stretta «Giudizio» della tabella l'etichetta lunga va a capo invece di uscire (prova in browser
 *  del 21/10: «Valore non indicato» fuori dalla tabella di 12-24 px tra 640 e 760 px); nei riquadri resta su una riga. */
function Pillola({ g, aCapo = false }: { g: Giudizio; aCapo?: boolean }) {
  const { testo, icona: Icona, classi } = PILLOLA[g];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded px-1.5 py-0.5 ${aCapo ? "" : "whitespace-nowrap"} ${classi}`}>
      <Icona className="w-3 h-3 shrink-0" aria-hidden="true" />
      {testo}
    </span>
  );
}

function Giudizi({ v }: { v: VoceFamiglia }) {
  const cliente = useVistaCliente();
  // al cliente solo le conclusioni: «Da verificare», «Non controllata», «Non verificabile» sono etichette del nostro
  // lavoro (Alfonso 19/10); la riga resta, coi suoi numeri, senza giudizio
  const giudizi = cliente ? v.giudizi.filter((x) => x !== "da_verificare") : v.giudizi;
  if (cliente && giudizi.length === 0) return null;
  if (v.giudizi.length === 0 && v.ufficiale === "prezzo_libero") return null;   // voce del venditore: niente da giudicare
  // esiti informativi: testo grigio, mai una pillola, per non competere con i giudizi (scelta di Alfonso 17/10, resa 1)
  if (v.giudizi.length === 0 && v.esito === "non_regolata") return null;
  if (v.giudizi.length === 0 && v.esito === "non_verificabile") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
        <History className="w-3 h-3" aria-hidden="true" />
        {ESTESA_NON_VERIFICABILE}
      </span>
    );
  }
  if (v.giudizi.length === 0) {
    return <span className="inline-flex text-xs font-semibold rounded px-1.5 py-0.5 text-slate-600 bg-slate-100 whitespace-nowrap">{ESTESA_NON_CONTROLLATA}</span>;
  }
  // una sola etichetta, la più forte (Alfonso 19/10)
  const g = [...new Set(giudizi)].sort((a, b) => ORDINE.indexOf(a) - ORDINE.indexOf(b)).slice(0, 1);
  // quanto pesa la voce segnalata (Alfonso 17/10): l'eccesso delle gonfiate, il costo delle altre. Riga del gas con la
  // G passata all'operatore: al cliente niente euro di una gonfiata non confermata (fix round 1 di B5)
  const inPiu = cliente && v.parte === "gas" && !v.giudizi.includes("G") ? null : v.euro_in_piu;
  const peso = inPiu ? `${circa(v.stimato)}${euro(inPiu)} ${ESTESA_EURO_IN_PIU}`
    : v.euro ? `${euro(v.euro)} ${VOCI_EURO_IN_BOLLETTA}`
      : !cliente && v.euro_non_spiegati ? `${euro(v.euro_non_spiegati)} ${VOCI_NON_SPIEGATI}` : null;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1">{g.map((x) => <Pillola key={x} g={x} aCapo />)}</div>
      {peso && <p className="text-xs tabular-nums font-semibold">{peso}</p>}
    </div>
  );
}

/** Prezzo in bolletta e valore ufficiale con gli stessi decimali quando si confrontano. */
function cifre(v: VoceFamiglia): { prezzo?: string; ufficiale?: string } {
  if (!v.prezzo || !v.ufficiale || v.ufficiale === "prezzo_libero") return {};
  const [prezzo, ufficiale] = cifreVoce(v.prezzo, v.ufficiale);
  return { prezzo, ufficiale };
}

function inBolletta(v: VoceFamiglia): { prezzo: string | null; importo: string | null } {
  return { prezzo: v.prezzo ? prezzoVoce(v.prezzo, v.unita, cifre(v).prezzo) : null, importo: v.importo ? euro(v.importo) : null };
}

function ufficiale(v: VoceFamiglia): string {
  if (v.ufficiale === "prezzo_libero") return ESTESA_PREZZO_LIBERO;
  if (v.esito === "non_regolata") return ESTESA_NON_REGOLATA;
  if (!v.ufficiale) return "—";
  return v.unita ? prezzoVoce(v.ufficiale, v.unita, cifre(v).ufficiale) : euro(v.ufficiale);
}

/** Le righe del motivo sotto una voce: una per la luce (codice tradotto), le frasi del backend per il gas. */
function perche(v: VoceFamiglia, conAvviso: boolean, cliente: boolean, gas: boolean): string[] {
  const rete = ["luce-quota-potenza", "luce-quota-fissa-rete", "luce-quota-energia-rete"];
  const una = (x: string | null) => (x ? [x] : []);
  if (v.giudizi.includes("N")) return una(motivoFuoriPosto(rete.includes(v.voce ?? "") ? "rete-e-oneri-nello-scontrino" : v.voce ?? ""));
  // gli altri motivi dicono perché una voce non ha giudizio: lavoro nostro, non per il cliente
  if (cliente) return [];
  // gas: le righe della bolletta non hanno euro accanto, «IVA esclusa» qui non si scrive
  return gas ? motiviGas(v.motivo).righe : una(motivoRiga(v.motivo, conAvviso));
}

/** id della famiglia: quello di sempre per la luce, col prefisso del gas nella dual (stesse chiavi) */
const idFamiglia = (f: Famiglia) => (f.parte === "gas" ? `fam-gas-${f.chiave}` : `fam-${f.chiave}`);

function TabellaFamiglia({ f, conAvviso, senzaGiudizio = false }: { f: Famiglia; conAvviso: boolean; senzaGiudizio?: boolean }) {
  const cliente = useVistaCliente();
  const colonne = senzaGiudizio ? ESTESA_COLONNE_SENZA_GIUDIZIO : ESTESA_COLONNE;
  const gas = f.parte === "gas";
  return (
    <section className="space-y-2" aria-labelledby={idFamiglia(f)}>
      <div className="flex items-baseline justify-between gap-3 border-b-2 border-brand/60 pb-1">
        <h3 id={idFamiglia(f)} className="font-heading text-lg font-semibold text-brand-dark">{f.nome}</h3>
        {f.totale && <span className="tabular-nums font-semibold whitespace-nowrap shrink-0">{euro(f.totale)}</span>}
      </div>
      {f.voci.length === 0 ? (
        <p className="text-sm text-muted-foreground py-1">{ESTESA_NESSUNA}</p>
      ) : (
        <>
          {/* computer: tabella come nel report */}
          <table className="hidden sm:table w-full text-sm table-fixed">
            <colgroup>{senzaGiudizio
              ? <><col className="w-[40%]" /><col className="w-[30%]" /><col className="w-[30%]" /></>
              : <><col className="w-[30%]" /><col className="w-[28%]" /><col className="w-[22%]" /><col className="w-[20%]" /></>}</colgroup>
            <thead className="text-left text-xs text-muted-foreground">
              <tr>{colonne.map((c) => <th key={c} scope="col" className="py-1 font-medium">{c}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-border">
              {f.voci.map((v, i) => <RigaTabella key={i} v={v} conAvviso={conAvviso} senzaGiudizio={senzaGiudizio} gas={gas} />)}
            </tbody>
          </table>
          {/* telefono: una scheda per voce */}
          <ul className="sm:hidden divide-y divide-border text-sm">
            {f.voci.map((v, i) => {
              const b = inBolletta(v);
              const motivi = perche(v, conAvviso, cliente, gas);
              return (
                <li key={i} className="py-2.5 space-y-1">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{nomeVoce(v.nome)}</span>
                    {b.importo && <span className="tabular-nums whitespace-nowrap">{b.importo}</span>}
                  </div>
                  {(b.prezzo || v.ufficiale || v.esito === "non_regolata") && (
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {[b.prezzo, v.esito === "non_regolata" ? ESTESA_NON_REGOLATA_TELEFONO
                        : v.ufficiale ? `${v.ufficiale === "prezzo_libero" ? "" : v.motivo === "formula" ? `${dallaFormula(v.perdite)} ` : "ufficiale "}${v.ufficiale === "prezzo_libero" ? ESTESA_PREZZO_LIBERO : v.unita === "%" ? prezzoVoce(v.ufficiale, "%", cifre(v).ufficiale) : cifre(v).ufficiale ?? cifraVoce(v.ufficiale)}` : null].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  {motivi.map((m) => <p key={m} className="text-xs text-muted-foreground">{m}</p>)}
                  {!senzaGiudizio && <Giudizi v={v} />}
                  <Perche testo={testoPerche(v.descrizione, v.stimato ? v.quota_picco : null, v.mancanti)} />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

function RigaTabella({ v, conAvviso, senzaGiudizio, gas }: { v: VoceFamiglia; conAvviso: boolean; senzaGiudizio: boolean; gas: boolean }) {
  const b = inBolletta(v);
  const motivi = perche(v, conAvviso, useVistaCliente(), gas);
  const { bottone, riga, aperto } = usePercheRiga(testoPerche(v.descrizione, v.stimato ? v.quota_picco : null, v.mancanti), senzaGiudizio ? 3 : 4);
  return (
    <Fragment>
      {/* aperta, la spiegazione fa parte della voce: nessuna linea fra le due (Tailwind 4: la linea sta sotto la riga) */}
      <tr className="align-top" style={aperto ? { borderBottomWidth: 0 } : undefined}>
        <td className="py-2 pr-2">
          {nomeVoce(v.nome)}
          {motivi.map((m) => <p key={m} className="text-xs text-muted-foreground mt-0.5">{m}</p>)}
          {bottone}
        </td>
        <td className="py-2 pr-2 tabular-nums">
          {b.prezzo}
          {b.importo && <span className={`block ${b.prezzo ? "text-muted-foreground" : ""}`}>{b.importo}</span>}
          {!b.prezzo && !b.importo && "—"}
        </td>
        <td className={`py-2 pr-2 tabular-nums ${v.ufficiale === "prezzo_libero" || !v.ufficiale ? "text-muted-foreground" : ""}`}>
          {ufficiale(v)}
          {v.motivo === "formula" && <span className="block text-xs text-muted-foreground">{dallaFormula(v.perdite)}</span>}
        </td>
        {!senzaGiudizio && <td className="py-2"><Giudizi v={v} /></td>}
      </tr>
      {riga}
    </Fragment>
  );
}

const TITOLO_SEZIONE = "flex items-baseline justify-between gap-3 border-b-2 border-brand/60 pb-1";

/** Le voci che l'AI segnala: nome, pillola, euro e «Perché» (versione B). Al cliente niente «Da verificare». */
function VociAi({ voci: tutte, dual }: { voci: Segnalazione[]; dual: boolean }) {
  const cliente = useVistaCliente();
  // al cliente mai una voce del gas «da verificare» (ruling 57): il backend la toglie già nel QR, qui anche per la
  // dual con la luce del motore
  const voci = tutte.filter((s) => !(cliente && s.parte === "gas" && s.giudizio === "da_verificare"));
  // nella dual le voci della luce e poi quelle del gas, ognuna col suo nome (ruling 62, come le famiglie)
  const [luce, gas] = [voci.filter((s) => s.parte !== "gas"), voci.filter((s) => s.parte === "gas")];
  // l'etichetta solo se ci sono voci di tutte e due le parti, o se la luce sta solo nelle famiglie (luce del motore)
  const conEtichette = dual && gas.length > 0;
  const gruppi = conEtichette ? [luce, gas] : [voci];
  return (
    <section className="space-y-2" aria-labelledby="ai-voci">
      <div className={TITOLO_SEZIONE}><h3 id="ai-voci" className="font-heading text-lg font-semibold text-brand-dark">{VOCI_DA_SEGNALARE}</h3></div>
      {gruppi.map((gruppo, n) => gruppo.length > 0 && (
      <Fragment key={n}>
      {conEtichette && <EtichettaParte gas={n === 1} />}
      <ul className="divide-y divide-border text-sm">
        {gruppo.map((s, i) => {
          // gas: i motivi del calcolo sono frasi del backend, solo per l'operatore; «IVA esclusa» accanto agli euro
          const gas = s.parte === "gas" ? motiviGas(s.motivo) : null;
          return (
            <li key={i} className="py-3 space-y-1.5">
              <div className="flex justify-between gap-3">
                <span className="font-medium">{nomeVoce(s.nome ?? s.dicitura)}</span>
                {s.euro_in_piu && (
                  <span className="tabular-nums font-semibold whitespace-nowrap">
                    {euro(s.euro_in_piu)}
                    {gas?.ivaEsclusa && !cliente && <span className="font-normal text-muted-foreground"> {IVA_ESCLUSA}</span>}
                  </span>
                )}
              </div>
              {!cliente && gas?.righe.map((m) => <p key={m} className="text-xs text-muted-foreground">{m}</p>)}
              {!(cliente && s.giudizio === "da_verificare") && <div><Pillola g={s.giudizio} /></div>}
              <Perche testo={s.descrizione} />
            </li>
          );
        })}
      </ul>
      </Fragment>
      ))}
    </section>
  );
}

/** «Luce» o «Gas» sopra un gruppo della dual: le parole e il disegno del riquadro «La bolletta» (ruling 62). */
function EtichettaParte({ gas }: { gas: boolean }) {
  const Icona = gas ? Flame : Zap;
  return (
    <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      <Icona className="h-3.5 w-3.5" aria-hidden="true" />
      {gas ? "Gas" : "Luce"}
    </p>
  );
}

/** Le note dell'AI: tema in grassetto, testo, euro a destra (versione B). */
function NoteAi({ note }: { note: NotaAnalisi[] }) {
  return (
    <section className="space-y-2" aria-labelledby="ai-note">
      <div className={TITOLO_SEZIONE}><h3 id="ai-note" className="font-heading text-lg font-semibold text-brand-dark">{NOTE_TITOLO}</h3></div>
      <ul className="divide-y divide-border text-sm">
        {note.map((n, i) => (
          <li key={i} className="py-2.5 flex justify-between gap-3">
            <span><span className="font-medium">{n.tema}.</span> <span className="text-muted-foreground">{n.perche}</span></span>
            {n.euro && /^-?\d+(\.\d+)?$/.test(n.euro) && <span className="tabular-nums whitespace-nowrap">{euro(n.euro)}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Vista estesa della fase 2 in una finestra sopra la pagina (a schermo intero sul telefono). */
export function ModaleBolletta({ dettaglio, commodity, periodoDa, periodoA, onClose, ritorno, dual: bollettaDual = false }: {
  dettaglio: Dettaglio; commodity?: string | null; periodoDa?: string | null; periodoA?: string | null; onClose: () => void;
  /** la bolletta ha luce e gas (dalle forniture della fase 1): solo allora le etichette «Luce» e «Gas» */
  dual?: boolean;
  /** dove riportare il fuoco alla chiusura (Safari non dà il fuoco al bottone cliccato) */
  ritorno?: RefObject<HTMLElement | null>;
}) {
  const chiudi = useRef<HTMLButtonElement>(null);
  const finestra = useRef<HTMLDivElement>(null);
  // onClose cambia a ogni aggiornamento del pannello (ogni secondo): l'effetto non deve ripartire, se no il fuoco
  // tornerebbe su «Chiudi» di continuo (classe deps-churn del CLAUDE.md)
  const suChiudi = useRef(onClose);
  useEffect(() => { suChiudi.current = onClose; });

  useEffect(() => {
    const prima = document.activeElement as HTMLElement | null;
    const bottone = ritorno?.current ?? null;
    chiudi.current?.focus();
    const tasto = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); suChiudi.current(); return; }
      if (e.key !== "Tab" || !finestra.current) return;
      // il fuoco resta dentro la finestra (ui-ux-pro-max: modal-escape, focus-management)
      const f = finestra.current.querySelectorAll<HTMLElement>("button, [href], [tabindex]:not([tabindex='-1'])");
      if (f.length === 0) return;
      const primo = f[0], ultimo = f[f.length - 1];
      if (e.shiftKey && document.activeElement === primo) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primo.focus(); }
    };
    document.addEventListener("keydown", tasto);
    const scorrimento = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", tasto);
      document.body.style.overflow = scorrimento;
      (bottone ?? prima)?.focus();
    };
  }, [ritorno]);   // un ref non cambia: l'effetto gira una volta sola

  const c = dettaglio.conteggi;
  // i riquadri contano l'etichetta che si vede: la più forte di ogni riga (una riga gonfiata e fuori posto conta 1 G)
  const forti = { G: 0, P: 0, N: 0, V: 0 } as Record<"G" | "P" | "N" | "V", number>;
  // «-ai» la luce (e la dual), «-ai-gas» la bolletta solo gas (B5)
  const ai = /-ai(-gas)?$/.test(dettaglio.versione ?? "");
  // dual con la luce del motore (AI della luce fallita): le voci del gas sono dell'AI, coi giudizi nelle segnalazioni
  const vociGas = ai ? [] : (dettaglio.segnalazioni ?? []).filter((s) => s.parte === "gas");
  // analisi dell'AI: le righe non hanno giudizi, i riquadri contano le voci segnalate
  const giudiziRighe = ai ? (dettaglio.segnalazioni ?? []).map((s) => [s.giudizio])
    : [...(dettaglio.famiglie ?? []).flatMap((f) => f.voci.map((v) => v.giudizi)), ...vociGas.map((s) => [s.giudizio])];
  for (const giudizi of giudiziRighe) {
    const forte = ORDINE.find((x) => giudizi.includes(x));
    if (forte === "G" || forte === "P" || forte === "N" || forte === "V") forti[forte] += 1;
  }
  // il riquadro di «V» solo quando c'è (21/10): le bollette senza restano coi tre di sempre
  const riquadri = (["G", "P", "N", "V"] as const).filter((g) => g !== "V" || forti.V > 0);
  const fam = (dettaglio.famiglie ?? []).filter((f) => f.voci.length > 0);
  const mese = periodoDa ? mesiBolletta(periodoDa, periodoA) : null;
  // «Luce, agosto 2026», non «luce · agosto 2026» (demo QR 18/10)
  const testa = [dettaglio.fornitore, commodity, mese].filter(Boolean).join(", ");
  const sotto = testa.charAt(0).toUpperCase() + testa.slice(1);
  const conEuro = Number(c?.euro_in_piu ?? 0) > 0;
  // tipo di cliente o periodo non scritti: detto una volta qui, non su ogni voce (avviso A, scelta di Alfonso 17/10)
  const dipendono = fam.some((f) => f.voci.some((v) => v.motivo === "tipo_cliente" || v.motivo === "periodo"));
  const cliente = useVistaCliente();
  const avviso = dipendono && !cliente && !ai ? avvisoMancano(dettaglio.mancano ?? []) : null;
  const vociAi = ai ? dettaglio.segnalazioni ?? [] : vociGas;
  // ruling 63: con l'analisi del gas non riuscita «In breve» non dice «nessuna da segnalare» (l'avviso dice cosa è successo)
  // ruling 68: anche per il cliente, che dal QR riceve solo il segno `gas_non_analizzato`
  const inBreveVisibile = !((dettaglio.ai_errore_gas || dettaglio.gas_non_analizzato) && c?.da_segnalare === 0);
  // ruling 62: nella dual «Luce» e «Gas» sopra i due gruppi di famiglie (le parole del riquadro «La bolletta»)
  // la dual la dice la bolletta (fix round 3). Le famiglie hanno l'etichetta solo se quelle del gas stanno a parte
  // (AI del gas riuscita): col motore intero una famiglia mescola righe della luce e del gas, e «Luce» mentirebbe
  const dual = bollettaDual;
  const famiglieDivise = dual && fam.some((f) => f.parte === "gas") && fam.some((f) => f.parte !== "gas");
  // con l'AI del gas fallita la finestra è quella del motore, ma la nota dei solleciti del gas resta (ruling 66)
  const noteAi = (ai || dettaglio.ai_errore_gas) && !cliente ? dettaglio.note ?? [] : [];
  // avvisi del gas in testa (scritte approvate da Alfonso il 23/10): icona e testo, mai il solo colore, letti dagli
  // screen reader (ui-ux-pro-max: color-not-only, aria-live); stesso disegno dell'avviso «tipo di cliente o periodo»
  const avvisiGas = [
    // ruling 67: come l'errore della luce, solo all'operatore
    dettaglio.ai_errore_gas && !cliente ? { testo: AI_ERRORE_GAS, icona: TriangleAlert, colore: "text-amber-700" } : null,
    dettaglio.motivo_luce ? { testo: avvisoLuce(dettaglio.motivo_luce), icona: FileX2, colore: "text-muted-foreground" } : null,
    // il periodo è un dato del nostro lavoro: solo all'operatore, come le note che sostituisce
    dettaglio.periodo_stampato && !cliente
      ? { testo: avvisoPeriodo(dettaglio.periodo_stampato), icona: Info, colore: "text-muted-foreground" } : null,
  ].filter((x) => x !== null);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/50 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div
        ref={finestra}
        role="dialog"
        aria-modal="true"
        aria-labelledby="estesa-titolo"
        onClick={(e) => e.stopPropagation()}
        className="bg-card w-full min-h-dvh sm:min-h-0 sm:max-w-[820px] sm:mx-auto sm:rounded-xl shadow-xl"
      >
        <div className="sticky top-0 z-10 bg-card flex items-start justify-between gap-4 px-4 sm:px-6 pt-4 pb-3 border-b border-border sm:rounded-t-xl">
          <div>
            <h2 id="estesa-titolo" className="font-heading text-xl font-semibold">{ESTESA_TITOLO}</h2>
            {sotto && <p className="text-sm text-muted-foreground">{sotto}</p>}
          </div>
          <button
            ref={chiudi}
            type="button"
            onClick={onClose}
            aria-label={ESTESA_CHIUDI}
            className="min-h-[44px] min-w-[44px] px-3 rounded-lg border border-border flex items-center justify-center gap-1.5 text-sm font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <X className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline" aria-hidden="true">{ESTESA_CHIUDI}</span>
          </button>
        </div>
        <div className="px-4 sm:px-6 py-5 space-y-5">
          {avvisiGas.length > 0 && (
            <div className="space-y-2">
              {avvisiGas.map(({ testo, icona: Icona, colore }) => (
                <p key={testo} role="status" className="text-sm flex items-start gap-2">
                  <Icona className={`w-4 h-4 mt-0.5 shrink-0 ${colore}`} aria-hidden="true" />
                  {testo}
                </p>
              ))}
            </div>
          )}
          {c && (
            <>
              {inBreveVisibile && (
                <p className="bg-surface border-l-4 border-brand rounded-r-lg px-4 py-3 text-[15px] leading-relaxed">
                  <b>In breve.</b> {inBreve(c.controllate, c.da_segnalare, c.principale)}
                </p>
              )}
              <div className={`grid grid-cols-2 gap-3 ${COLONNE[riquadri.length + (conEuro ? 1 : 0)]}`}>
                {riquadri.map((g, i) => (
                  // riquadri dispari sul telefono: l'ultimo prende la riga intera invece di lasciare un buco accanto
                  <div key={g} className={`border border-border rounded-lg px-3 py-2 ${i === riquadri.length - 1 && !conEuro && riquadri.length % 2 === 1 ? A_TUTTA_RIGA : ""}`}>
                    <p className="font-heading text-2xl font-semibold tabular-nums">{forti[g]}</p>
                    <Pillola g={g} />
                  </div>
                ))}
                {/* a zero non dice niente e accanto a una voce da segnalare confonde: non si mostra (demo QR 18/10) */}
                {conEuro && c.euro_in_piu && (
                  <div className={`border border-border rounded-lg px-3 py-2 ${riquadri.length % 2 === 0 ? A_TUTTA_RIGA : ""}`}>
                    <p className="font-heading text-2xl font-semibold tabular-nums">
                      {/* totale con € stimati: «circa» a peso normale, la cifra resta in evidenza */}
                      {c.euro_stimato && <span className="text-base font-normal">{circa(true)}</span>}
                      {euro(c.euro_in_piu)}
                    </p>
                    <span className="text-xs text-muted-foreground">{ESTESA_EURO_IN_PIU}</span>
                  </div>
                )}
              </div>
            </>
          )}
          {avviso && (
            <p className="text-sm flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
              {avviso}
            </p>
          )}
          {vociAi.length > 0 && <VociAi voci={vociAi} dual={dual} />}
          {noteAi.length > 0 && <NoteAi note={noteAi} />}
          {ai && fam.length > 0 && <h3 className="font-heading text-xl font-semibold">{RIGHE_TITOLO}</h3>}
          {/* nella dual le famiglie della luce e poi quelle del gas, con le stesse chiavi: la chiave dice anche la parte */}
          {fam.map((f, i) => {
            const gas = f.parte === "gas";
            // l'etichetta della parte davanti alla prima famiglia di ognuna (stesso disegno di «La bolletta»)
            const etichetta = famiglieDivise && (i === 0 || (fam[i - 1].parte === "gas") !== gas);
            return (
              <Fragment key={idFamiglia(f)}>
                {etichetta && <EtichettaParte gas={gas} />}
                {/* le famiglie del gas non hanno giudizi (stanno nelle sue voci): mai la colonna Giudizio */}
                <TabellaFamiglia f={f} conAvviso={avviso !== null} senzaGiudizio={ai || gas} />
              </Fragment>
            );
          })}
          <p className="text-xs text-muted-foreground border-t border-border pt-3">{ESTESA_NOTA}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
